import React from "react";
import { motion } from "framer-motion";
import { Cpu, Factory, ShieldCheck, Zap, HeartPulse, CheckCircle2, Sprout } from "lucide-react";

// --- PHOTO INSTRUCTIONS ---
import heroImg from "../about-images/deeptech.png"; 
import mentorImg from "../about-images/newimpact.jpg"; 

// --- ANIMATION VARIANTS ---
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const About = () => {
  return (
    <div className="font-sans text-slate-800 bg-white selection:bg-blue-100 selection:text-blue-900">

      {/* --- 1. HERO SECTION --- */}
      <div className="relative h-[75vh] min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <motion.div 
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 10, ease: "linear" }}
            className="w-full h-full"
          >
            <img 
                src={heroImg} 
                alt="IIT Ropar TBIF Infrastructure" 
                className="w-full h-full object-cover" 
            />
          </motion.div>
          <div className="absolute inset-0 bg-slate-900/50"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
        </div>

        <motion.div 
          initial="hidden" animate="visible" variants={fadeInUp}
          className="relative z-10 text-center px-4 max-w-5xl mx-auto -mt-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-8 shadow-lg cursor-default">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
            </span>
            <span className="text-blue-50 text-xs font-bold tracking-widest uppercase">
              Official Incubation Partner • DST, Govt. of India
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-extrabold text-white leading-tight mb-6 tracking-tight drop-shadow-2xl">
            Building India's <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Deep-Tech</span> Future
          </h1>
          
          <p className="text-slate-200 text-lg md:text-xl max-w-3xl mx-auto font-light leading-relaxed drop-shadow-md">
           IIT Ropar- TBIF is the bridge between academic research and commercial success, providing a launchpad for the next generation of innovators.
          </p>
        </motion.div>
      </div>

      {/* --- 2. FLOATING IMPACT STATS --- */}
      <div className="relative z-20 px-6 -mt-24 mb-20">
        <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="max-w-6xl mx-auto bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50 p-10 grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-slate-100"
        >
            <StatItem value="300+" label="Startups Incubated" delay={0} />
            <StatItem value="₹1600 Cr+" label="Total Valuation" delay={0.1} />
            <StatItem value="₹350 Cr+" label="External Funding" delay={0.2} />
            <StatItem value="20k" label="Sq. Ft. Facility" delay={0.3} />
        </motion.div>
      </div>

      {/* --- 3. THE "WHO WE ARE" --- */}
      <section className="py-20 px-6 lg:px-12 max-w-7xl mx-auto relative">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div 
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
            >
                <div className="flex items-center gap-2 mb-4">
                    <div className="h-1 w-12 bg-blue-600 rounded-full"></div>
                    <span className="text-blue-600 font-bold uppercase text-sm tracking-widest">Our Story</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                    Where Research Meets <span className="text-blue-600">Real-World Impact</span>
                </h2>
                <p className="text-slate-600 text-lg leading-relaxed mb-6">
                    The IIT Ropar Technology Business Incubator Foundation (TBIF) is a Section 8 non-profit established under the <strong>NIDHI TBI Scheme</strong>.
                </p>
                <p className="text-slate-600 text-lg leading-relaxed mb-8">
                    Our mission is to <strong>Enable and Empower</strong>. We provide an ecosystem with over <strong>180+ Mentors</strong>, including industry stalwarts and senior army veterans.
                </p>
                <div className="bg-gradient-to-r from-blue-50 to-white border-l-4 border-blue-600 p-8 rounded-r-xl shadow-sm cursor-default transition-transform duration-300 hover:translate-x-2">
                    <p className="italic text-slate-700 font-medium text-lg">
                        "Innovation is the key to realizing the dream of 'New India'. We invest in technology to create a self-reliant future."
                    </p>
                </div>
            </motion.div>

            <motion.div 
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative group"
            >
                <div className="absolute -inset-4 bg-gradient-to-tr from-blue-200 to-cyan-200 rounded-2xl transform rotate-2 group-hover:rotate-0 transition-transform duration-500 opacity-60"></div>
                <img 
                    src={mentorImg} 
                    alt="Mentorship Session" 
                    className="relative rounded-xl shadow-2xl w-full object-cover h-[500px]"
                />
            </motion.div>
        </div>
      </section>

      {/* --- 4. IMPROVED THRUST AREAS (Professional & Technical) --- */}
      <section className="py-24 bg-slate-50 relative overflow-hidden mt-10 border-t border-slate-200">
        
        {/* Technical Background Pattern */}
        <div className="absolute inset-0 opacity-[0.4]" style={{ backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-50/50 to-transparent"></div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
            <motion.div 
    variants={staggerContainer}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    className="grid md:grid-cols-2 lg:grid-cols-6 gap-6"
>
    <div className="lg:col-span-2">
        <FocusCard 
            title="Artificial Intelligence & Machine Learning"
            icon={Cpu}
            items={["True AI Systems", "Smart IP Cameras", "Predictive Analytics"]}
        />
    </div>

    <div className="lg:col-span-2">
        <FocusCard 
            title="Healthcare Technologies"
            icon={HeartPulse}
            items={["Neonatal Beds", "ICU Isolation Units", "Smart Health Kiosks"]}
        />
    </div>

    <div className="lg:col-span-2">
        <FocusCard 
            title="Manufacturing"
            icon={Factory}
            items={["Sustainable Materials", "Robotic Arms", "Smart Factory Solutions"]}
        />
    </div>

    <div className="lg:col-start-2 lg:col-span-2">
        <FocusCard 
            title="Defence & Security"
            icon={ShieldCheck}
            items={["Gyro Chassis for T90", "SIFCON Panels", "Surveillance Drones"]}
        />
    </div>

    <div className="lg:col-span-2">
        <FocusCard 
            title="Energy & Waste"
            icon={Zap}
            items={["Hydrogen Dispensing", "Waste Pyrolysis", "Clean Energy Tech"]}
        />
    </div>
</motion.div>
        </div>
      </section>

      {/* --- 5. CALL TO ACTION --- */}
      <div className="py-24 bg-white text-center px-6 relative overflow-hidden">
          <div className="max-w-4xl mx-auto relative z-10">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}>
                <h2 className="text-4xl font-bold text-slate-900 mb-6">Ready to Scale Your Idea?</h2>
                <p className="text-slate-600 mb-10 max-w-xl mx-auto text-lg">
                    Join the cohort of 300+ startups transforming the world from IIT Ropar.
                </p>
                <motion.a 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href="https://forms.gle/Fz2pn47EqTscZK4T8"
                    className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-5 rounded-full font-bold text-lg shadow-xl shadow-blue-600/30 transition-all"
                >
                    Apply for Incubation
                </motion.a>
            </motion.div>
          </div>
      </div>

    </div>
  );
};

// --- SUB-COMPONENTS ---

const StatItem = ({ value, label, delay }) => (
    <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: delay, duration: 0.5 }}
        className="text-center p-4 hover:scale-105 transition-transform duration-300"
    >
        <div className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-blue-500">
            {value}
        </div>
        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{label}</div>
    </motion.div>
);

// --- REFINED PROFESSIONAL CARD (Blue & Slate Only) ---
const FocusCard = ({ title, items, icon: Icon }) => {
    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
    };

    return (
        <motion.div 
            variants={cardVariants}
            whileHover={{ y: -5, borderColor: '#3b82f6' }}
            className="group bg-white p-8 rounded-xl shadow-sm border border-slate-200 transition-all duration-300 hover:shadow-xl hover:shadow-blue-900/5 relative overflow-hidden"
        >
            {/* Top accent line on hover */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-cyan-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>

            <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-slate-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                    <Icon size={24} strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold text-slate-800 group-hover:text-blue-700 transition-colors">{title}</h3>
            </div>
            
            <ul className="space-y-3">
                {items.map((item, i) => (
                    <li key={i} className="flex items-start text-slate-600 text-sm font-medium">
                        <CheckCircle2 className="w-4 h-4 text-blue-400 mt-0.5 mr-3 shrink-0" />
                        <span className="group-hover:text-slate-900 transition-colors">{item}</span>
                    </li>
                ))}
            </ul>
        </motion.div>
    );
};

export default About;